/**
 * Design tokens — single source of truth for the visual language.
 *
 * Philosophy: "Stille Präzision" (see docs/redesign/design-philosophy.md).
 * One calm teal carries the identity; everything else is restrained neutrals.
 * Motion tokens drive framer-motion variants across the app.
 */
export const tokens = {
  color: {
    brand: {
      50: '#eef6f6',
      100: '#d4e8e8',
      200: '#a9d1d1',
      300: '#7ebaba',
      400: '#4d9494',
      500: '#2d6a6a',
      600: '#255858',
      700: '#1d4646',
      800: '#153434',
      900: '#0d2222',
    },
    surface: {
      /** Page background — cool architectural concrete-green. */
      canvas: '#eef1ef',
      /** Raised wells, table heads, panel headers (between paper and canvas). */
      panel: '#f7f9f8',
      /** Recessed areas — the plan-canvas frame, progress tracks. */
      well: '#e7ebe9',
      /** Standard light hairline border (replaces 1px divider on elevated surfaces). */
      hairline: 'rgba(15, 38, 38, 0.10)',
      /** Hairline for floating elements over busy content. */
      hairlineStrong: 'rgba(15, 38, 38, 0.16)',
      /** Inner top edge — the "milled bevel" highlight, light mode. */
      highlight: 'rgba(255, 255, 255, 0.75)',
      /** Dialog backdrop tint (teal-black, not pure black). */
      scrim: 'rgba(13, 34, 34, 0.36)',
      /** Page background */
      darkCanvas: '#111618',
      /** Cards, header, tables */
      darkPaper: '#242e2f',
      /** Inputs, table headers, wells */
      darkElevated: '#2f3b3c',
      /** Visible borders on dark UI */
      darkBorder: '#455a59',
      /** Recessed plan frame in dark. */
      darkWell: '#0d1213',
      darkHairline: 'rgba(255, 255, 255, 0.08)',
      /** Inner top edge, dark mode (very faint). */
      darkHighlight: 'rgba(255, 255, 255, 0.06)',
      darkScrim: 'rgba(0, 0, 0, 0.55)',
    },
    text: {
      darkPrimary: '#f4f8f7',
      darkSecondary: '#c5d4d3',
      darkDisabled: '#8aa09f',
    },
    neutral: {
      0: '#ffffff',
      50: '#f4f6f2',
      100: '#e9ece6',
      200: '#d5dad2',
      300: '#aeb9b8',
      400: '#7f8c8b',
      500: '#5c6b6a',
      600: '#455352',
      700: '#323d3c',
      800: '#1f2625',
      900: '#0f1212',
    },
    /** Warm reward accent — reserved for success/progress moments (scan done, review complete). */
    accent: { main: '#d97706', light: '#fbbf24', dark: '#b45309', surface: '#fef3c7', contrastText: '#ffffff' },
    /** green-700: white on it (and it as text on white) meets WCAG AA 4.5:1; #16a34a was 3.3:1. */
    success: { main: '#15803d', light: '#4ade80', dark: '#166534', contrastText: '#ffffff' },
    warning: { main: '#ca8a04', light: '#facc15', dark: '#a16207', contrastText: '#111111' },
    error: { main: '#dc2626', light: '#f87171', dark: '#991b1b', contrastText: '#ffffff' },
    info: { main: '#2d6a6a', light: '#7ebaba', dark: '#1d4646', contrastText: '#ffffff' },
  },
  radius: { sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, pill: 999 },
  spacing: 8,
  font: {
    family: '"Inter", "Roboto", system-ui, -apple-system, "Segoe UI", sans-serif',
    familyMono: '"JetBrains Mono", ui-monospace, SFMono-Regular, monospace',
    sizeBase: 14,
  },
  /**
   * Layered material shadows — every raised surface follows one recipe:
   * inner top highlight ("milled bevel") + key shadow + ambient shadow.
   * Dark mode swaps highlights for hairline rings.
   */
  shadow: {
    /** Hairline ring — crisper than a border on HiDPI, no layout shift. */
    ring: '0 0 0 1px rgba(15, 38, 38, 0.10)',
    ringStrong: '0 0 0 1px rgba(15, 38, 38, 0.16)',

    sm: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 1px 2px rgba(15,38,38,0.06)',
    md: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 1px 2px rgba(15,38,38,0.06), 0 6px 16px -4px rgba(15,38,38,0.10)',
    lg: 'inset 0 1px 0 rgba(255,255,255,0.75), 0 2px 4px rgba(15,38,38,0.07), 0 12px 28px -6px rgba(15,38,38,0.14), 0 24px 56px -16px rgba(15,38,38,0.12)',

    card: 'inset 0 1px 0 rgba(255,255,255,0.70), 0 1px 2px rgba(15,38,38,0.05), 0 6px 16px -4px rgba(15,38,38,0.08), 0 12px 32px -10px rgba(15,38,38,0.06)',
    /** Hover lift for interactive cards. */
    cardHover: 'inset 0 1px 0 rgba(255,255,255,0.70), 0 2px 4px rgba(15,38,38,0.06), 0 10px 24px -4px rgba(15,38,38,0.11), 0 20px 48px -12px rgba(15,38,38,0.10)',

    /** Floating clay surfaces over the plan canvas (toolbar, panels, review bar). */
    float: '0 0 0 1px rgba(15,38,38,0.10), inset 0 1px 0 rgba(255,255,255,0.80), 0 2px 6px rgba(15,38,38,0.08), 0 12px 32px -8px rgba(15,38,38,0.16)',
    /** Dialogs and side overlays. */
    overlay: '0 0 0 1px rgba(15,38,38,0.08), inset 0 1px 0 rgba(255,255,255,0.85), 0 4px 12px rgba(15,38,38,0.08), 0 24px 64px -16px rgba(15,38,38,0.22)',
    /** Recessed wells (plan frame, progress tracks, segmented controls). */
    insetWell: 'inset 0 1px 3px rgba(15,38,38,0.08), inset 0 0 0 1px rgba(15,38,38,0.05)',

    darkRing: '0 0 0 1px rgba(255,255,255,0.08)',
    darkCard: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.35)',
    darkMd: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 20px rgba(0,0,0,0.5)',
    darkFloat: '0 0 0 1px rgba(255,255,255,0.10), inset 0 1px 0 rgba(255,255,255,0.06), 0 12px 32px -8px rgba(0,0,0,0.6)',
    darkOverlay: '0 0 0 1px rgba(255,255,255,0.10), 0 24px 64px -16px rgba(0,0,0,0.7)',
    darkInsetWell: 'inset 0 1px 3px rgba(0,0,0,0.5), inset 0 0 0 1px rgba(255,255,255,0.04)',
  },
  /** Restrained material gradients — "anodized", never glossy. */
  gradient: {
    /** Primary buttons, light mode. */
    primary: 'linear-gradient(180deg, #357575 0%, #2d6a6a 55%, #285f5f 100%)',
    primaryHover: 'linear-gradient(180deg, #3b7e7e 0%, #316f6f 55%, #2b6464 100%)',
    primaryActive: 'linear-gradient(180deg, #285f5f 0%, #285f5f 100%)',
    /** The instrument rail — dark graphite-teal in BOTH color modes. */
    rail: 'linear-gradient(180deg, #182221 0%, #121a19 100%)',
    /** Active nav pill on the dark rail. */
    navPill: 'linear-gradient(180deg, #347373 0%, #2d6a6a 100%)',
  },
  /** Premium focus ring (offset ring on the page surface). */
  focusRing: {
    light: '0 0 0 2px #eef1ef, 0 0 0 4px rgba(45, 106, 106, 0.6)',
    dark: '0 0 0 2px #111618, 0 0 0 4px rgba(126, 186, 186, 0.7)',
  },
} as const;

/**
 * Motion tokens — the choreography contract (docs/redesign/04-motion-map.png).
 *
 * - micro: hover/focus/press feedback
 * - standard: panels, dialogs, route transitions
 * - data: count-ups and chart growth
 * - Springs animate arrival (cards, markers, reward moments)
 * - Durations are seconds (framer-motion convention)
 *
 * Rules: motion explains causality, never decorates. Only `transform` and
 * `opacity`. `prefers-reduced-motion` disables everything (see useMotionSafe).
 */
export const motion = {
  duration: { micro: 0.18, standard: 0.22, data: 0.6 },
  /** CSS-side durations in ms for MUI transitions. */
  durationMs: { micro: 180, standard: 220 },
  ease: {
    out: [0.16, 1, 0.3, 1] as const,
    standard: [0.4, 0, 0.2, 1] as const,
  },
  /** Settle-into-place spring for cards, markers, badges. */
  spring: { type: 'spring' as const, stiffness: 380, damping: 30, mass: 0.9 },
  /** Softer spring for the reward moment (marker pops). */
  springReward: { type: 'spring' as const, stiffness: 300, damping: 22, mass: 0.8 },
  /** Delay between staggered children (seconds). */
  stagger: 0.04,
} as const;

export type Tokens = typeof tokens;
export type MotionTokens = typeof motion;
